"use client";

import { useEffect, useState } from "react";
import {
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalHeader,
  ModalTitle,
} from "@/components/modal";
import { ScrollArea } from "@/components/ui/scroll-area";
import ScriptActions from "./actions";
import { Scrollbar } from "@radix-ui/react-scroll-area";
import { Loader2 } from "lucide-react";

type ScriptViewerProps = {
  slug: string;
  loadstring: string;
};

export default function ScriptViewer(props: ScriptViewerProps) {
  const [data, setData] = useState<{
    html: string;
    script: string;
    numberOfLines: number;
    linesOfSourceCode: number;
    formattedSize: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function loadScript() {
      try {
        const response = await fetch(
          `/api/script/highlight?loadstring=${encodeURIComponent(props.loadstring)}`,
        );

        if (!response.ok) {
          throw new Error("Failed to load script");
        }

        const result = await response.json();

        if (!cancelled) {
          setData(result);
          setIsLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Failed to load script",
          );
          setIsLoading(false);
        }
      }
    }

    loadScript();

    return () => {
      cancelled = true;
    };
  }, [props.loadstring]);

  return (
    <ModalContent className="bg-glassmorphism flex max-w-3xl flex-col space-y-1">
      <ModalHeader>
        <ModalTitle>Script Viewer</ModalTitle>
        <ModalDescription>View the source code of this script</ModalDescription>
      </ModalHeader>
      <ModalBody>
        <div className="mb-4 rounded-md border border-white/5 md:mb-0">
          {/* Controls */}
          <div className="flex items-center justify-between space-x-2 rounded-t-md bg-white/5 p-2 pl-4">
            {data ? (
              <p className="font-mono text-xs text-muted-foreground">
                {data.numberOfLines} line{data.numberOfLines === 1 ? "" : "s"} (
                {data.linesOfSourceCode} loc) · {data.formattedSize}
              </p>
            ) : (
              <p className="font-mono text-xs text-muted-foreground">
                Loading...
              </p>
            )}

            <div className="flex flex-row items-center space-x-0.5">
              {data && (
                <ScriptActions
                  script={data.script}
                  loadstring={props.loadstring}
                />
              )}
            </div>
          </div>

          {/* Content */}
          <ScrollArea className="h-[400px] rounded-b-md bg-[#0a0a0d] p-4">
            <Scrollbar orientation="horizontal" />
            <Scrollbar orientation="vertical" />

            {error ? (
              <div className="flex h-full items-center justify-center text-red-500">
                {error}
              </div>
            ) : isLoading ? (
              <div className="flex h-full items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : data ? (
              <div dangerouslySetInnerHTML={{ __html: data.html }} />
            ) : null}
          </ScrollArea>
        </div>
      </ModalBody>
    </ModalContent>
  );
}
